import type { IconProps } from '@tabler/icons-react';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconBell,
  IconBuilding,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconClock,
  IconCode,
  IconCopy,
  IconDatabase,
  IconDiscount2,
  IconDroplet,
  IconDotsVertical,
  IconDownload,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconFile,
  IconFilter,
  IconFolder,
  IconKey,
  IconLayoutDashboard,
  IconLink,
  IconLoader2,
  IconLock,
  IconLockOpen,
  IconLogout,
  IconMail,
  IconMenu2,
  IconMoon,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSettings,
  IconShield,
  IconShoppingCart,
  IconSortAscending,
  IconStack2,
  IconSun,
  IconTicket,
  IconTrash,
  IconTrendingUp,
  IconUpload,
  IconUser,
  IconUsers,
  IconX,
} from '@tabler/icons-react';

export const tablerIcons = {
  alertCircle: IconAlertCircle,
  alertTriangle: IconAlertTriangle,
  arrowLeft: IconArrowLeft,
  arrowRight: IconArrowRight,
  bell: IconBell,
  building: IconBuilding,
  chart: IconChartBar,
  check: IconCheck,
  chevronDown: IconChevronDown,
  chevronLeft: IconChevronLeft,
  chevronRight: IconChevronRight,
  chevronUp: IconChevronUp,
  clock: IconClock,
  code: IconCode,
  copy: IconCopy,
  database: IconDatabase,
  discount: IconDiscount2,
  droplet: IconDroplet,
  dotsVertical: IconDotsVertical,
  download: IconDownload,
  external: IconExternalLink,
  eye: IconEye,
  eyeOff: IconEyeOff,
  file: IconFile,
  filter: IconFilter,
  folder: IconFolder,
  key: IconKey,
  link: IconLink,
  loader: IconLoader2,
  lock: IconLock,
  lockOpen: IconLockOpen,
  logout: IconLogout,
  mail: IconMail,
  menu: IconMenu2,
  moon: IconMoon,
  nav: IconLayoutDashboard,
  pencil: IconPencil,
  plus: IconPlus,
  refresh: IconRefresh,
  search: IconSearch,
  settings: IconSettings,
  shield: IconShield,
  shopping: IconShoppingCart,
  sort: IconSortAscending,
  stack: IconStack2,
  sun: IconSun,
  ticket: IconTicket,
  trash: IconTrash,
  trending: IconTrendingUp,
  upload: IconUpload,
  user: IconUser,
  users: IconUsers,
  x: IconX,
} as const;

export type IconName = keyof typeof tablerIcons;

export const iconNames = Object.keys(tablerIcons) as IconName[];

export const iconSizes = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 24,
} as const;

export type IconSize = keyof typeof iconSizes;

export interface LuaIconProps extends Omit<IconProps, 'size'> {
  name: IconName;
  size?: IconSize | number;
  strokeWidth?: number;
  label?: string;
}

export function Icon({ name, size = 'md', strokeWidth = 1.75, label, ...rest }: LuaIconProps) {
  const Component = tablerIcons[name];
  const resolved = typeof size === 'number' ? size : iconSizes[size];
  return (
    <Component
      size={resolved}
      stroke={strokeWidth}
      aria-label={label}
      role={label ? 'img' : undefined}
      aria-hidden={label ? undefined : true}
      focusable={false}
      {...rest}
    />
  );
}

export {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconKey,
  IconLoader2,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconSettings,
  IconTrash,
  IconUser,
  IconUsers,
  IconX,
};
export type { IconProps };
